// The log line of a click a hard round's board turns away because it has
// locked (rev 4, DESIGN §6; staging audit 1: a Coin Flip click refused at the
// lock logged nothing). The board's behaviour is unchanged; this only says it
// happened, and how long after the lock. The lock is read as the board reads
// it (`hardCloseClock`): the first of `race_starting`'s arrival and the
// countdown, on this page's `performance.now()`.
import { datadogLogs } from '@datadog/browser-logs'
import type { Race } from 'src/types/domain/Race'
import { hardCloseClock, perfNow } from './hardCloseClock'

/** `click`: the click panel (Coin Flip). `chip`: the chip panel. */
export type RefusedClickBoard = 'click' | 'chip'

/**
 * What became of the click: the click panel opened its "next game" panel, or
 * queued the bet for the next game; the chip panel kept the chip for the next
 * round.
 */
export type RefusedClickThen = 'next_game_panel' | 'queued_next_game' | 'next_round'

/**
 * Log a click refused at the lock of the round on the board. True when it
 * logged: the round is hard and its board has locked. A round that is not
 * hard, or one not locked yet, logs nothing.
 */
export function logClickRefusedAtLock(input: {
  board: RefusedClickBoard
  then: RefusedClickThen
  race: Pick<Race, 'uuid' | 'raceConfigurationId' | 'raceEvents'> | undefined
  previousRace: Pick<Race, 'raceEvents'> | undefined
  now?: number
}): boolean {
  const { race } = input
  const now = input.now ?? perfNow()
  const clock = hardCloseClock(race, input.previousRace, now)
  if (!race || clock?.lockedBy === undefined) return false
  datadogLogs.logger.info('hard_close_click_refused', {
    user_event: {
      roundId: race.uuid,
      gameId: race.raceConfigurationId,
      board: input.board,
      then: input.then,
      lockedBy: clock.lockedBy,
      msAfterLock: clock.lockedAt !== undefined ? Math.round(now - clock.lockedAt) : undefined
    }
  })
  return true
}

/**
 * The chip panel's line (`then: 'next_round'`): a chip or Repeat laid on a hard
 * round's board after its lock while the round is still open, once a round. It
 * runs first in every chip tap, so it never throws: a board with no round yet
 * logs nothing. Returns the round it has logged for (`loggedRoundId` as given
 * when it did not log).
 */
export function logChipAfterLockOnce(input: {
  race: Pick<Race, 'uuid' | 'raceConfigurationId' | 'raceEvents'> | undefined
  previousRace: Pick<Race, 'raceEvents'> | undefined
  phase: string | undefined
  loggedRoundId: string | undefined
  now?: number
}): string | undefined {
  const { race, loggedRoundId } = input
  if (!race || loggedRoundId === race.uuid) return loggedRoundId
  if (input.phase !== 'race_ready' && input.phase !== 'race_starting') return loggedRoundId
  const logged = logClickRefusedAtLock({
    board: 'chip',
    then: 'next_round',
    race,
    previousRace: input.previousRace,
    now: input.now
  })
  return logged ? race.uuid : loggedRoundId
}
